import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Loader2,
  Lock,
  RotateCcw,
  Search,
  Trash2,
  Trophy,
  Upload,
  Pin,
  X,
  XCircle,
} from 'lucide-react';
import {
  ApiError,
  api,
  RANK_LABEL,
  canEdit,
  type AuthUser,
  type VsDailyScore,
  type VsDetail,
  type VsImportVideoJob,
  type VsDetailPlayer,
  type VsKind,
  type VsOutcome,
  type VsTopPlayerEntry,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { EventsTabs } from './Events';
import { KindPill } from './EventsVs';
import ImportVsDialog from '@/components/ImportVsDialog';
import { DAY_LABELS as DAY_LABELS_UPPER, formatMillionsOneDecimal, parseMillionsOneDecimal } from '@/lib/vsFormat';

const RANK_ORDER: Record<string, number> = { R5: 0, R4: 1, R3: 2, R2: 3, R1: 4 };

export default function EventsVsDetail({ user }: { user: AuthUser }) {
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();

  const detailQuery = useQuery({
    queryKey: ["vs", id],
    queryFn: () => api.getVs(id),
    enabled: Number.isInteger(id) && id > 0,
  });

  if (!canEdit(user.role)) {
    return (
      <div className="space-y-4">
        <EventsTabs user={user} />
        <div className="text-slate-400">Only R4+ can view VS.</div>
      </div>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4">
        <EventsTabs user={user} />
        <div className="text-center text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!detailQuery.data) {
    return (
      <div className="space-y-4">
        <EventsTabs user={user} />
        <div className="text-slate-400">VS entry not found.</div>
        <Link to="/events/vs" className="text-indigo-400 hover:underline">
          Back to VS list
        </Link>
      </div>
    );
  }

  return (
    <Detail
      user={user}
      detail={detailQuery.data}
      onDeleted={() => navigate('/events/vs')}
    />
  );
}

type RowState = 'idle' | 'saving' | 'saved' | 'error';

function Detail({
  user,
  detail,
  onDeleted,
}: {
  user: AuthUser;
  detail: VsDetail;
  onDeleted: () => void;
}) {
  const { vs: vsWeek } = detail;
  const qc = useQueryClient();
  const locked = !!vsWeek.lockedAt;

  const activePlayers = useMemo(
    () =>
      [...detail.players]
        .filter((p) => p.active)
        .sort((a, b) => {
          const ra = RANK_ORDER[a.rank] ?? 99;
          const rb = RANK_ORDER[b.rank] ?? 99;
          if (ra !== rb) return ra - rb;
          return (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name);
        }),
    [detail.players],
  );

  const serverScores = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of detail.scores) map.set(s.playerId, s.scoreM);
    return map;
  }, [detail.scores]);

  const updatedAtMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of detail.scores) map.set(s.playerId, s.updatedAt);
    return map;
  }, [detail.scores]);

  const [inputs, setInputs] = useState<Map<number, string>>(() => {
    const map = new Map<number, string>();
    for (const s of detail.scores) map.set(s.playerId, String(s.scoreM));
    return map;
  });
  useEffect(() => {
    setInputs(() => {
      const map = new Map<number, string>();
      for (const s of detail.scores) map.set(s.playerId, String(s.scoreM));
      return map;
    });
  }, [detail.scores]);

  const [view, setView] = useState<'weekly' | 'daily'>('daily');
  const [importOpen, setImportOpen] = useState(false);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const jobsQuery = useQuery({
    queryKey: ['vs-import-jobs', vsWeek.id],
    queryFn: () => api.listVsImportJobs(vsWeek.id),
    refetchInterval: (q) => {
      const pending = q.state.data?.jobs.some(
        (j) => j.status === 'queued' || j.status === 'running',
      );
      return pending ? 2500 : false;
    },
  });
  const jobs = (jobsQuery.data?.jobs ?? []).filter((j) => !j.appliedAt);
  const [rowState, setRowState] = useState<Map<number, RowState>>(new Map());
  const setRow = (playerId: number, state: RowState) =>
    setRowState((prev) => {
      const next = new Map(prev);
      if (state === 'idle') next.delete(playerId);
      else next.set(playerId, state);
      return next;
    });

  const total = useMemo(() => {
    let sum = 0;
    let count = 0;
    for (const val of inputs.values()) {
      const n = Number(val);
      if (Number.isFinite(n) && val !== '') {
        sum += n;
        count += 1;
      }
    }
    return { sum, count };
  }, [inputs]);

  const saveRow = async (playerId: number, raw: string) => {
    const previousRaw = serverScores.has(playerId)
      ? String(serverScores.get(playerId))
      : '';
    if (raw === previousRaw) {
      setRow(playerId, 'idle');
      return;
    }
    const scoreM =
      raw === '' ? null : Number.isFinite(Number(raw)) ? Math.trunc(Number(raw)) : null;
    if (raw !== '' && (scoreM === null || scoreM < 0)) {
      setRow(playerId, 'error');
      return;
    }
    setRow(playerId, 'saving');
    try {
      await api.saveVsScores(vsWeek.id, [{ playerId, scoreM }]);
      setRow(playerId, 'saved');
      qc.invalidateQueries({ queryKey: ["vs", vsWeek.id] });
      qc.invalidateQueries({ queryKey: ["vs"] });
      setTimeout(() => setRow(playerId, 'idle'), 1500);
    } catch (err) {
      setRow(playerId, 'error');
      if (err instanceof ApiError && err.status === 423) {
        qc.invalidateQueries({ queryKey: ["vs", vsWeek.id] });
      }
    }
  };

  const setOutcome = useMutation({
    mutationFn: (outcome: VsOutcome | null) => api.updateVs(vsWeek.id, { outcome }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vs", vsWeek.id] });
      qc.invalidateQueries({ queryKey: ["vs"] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteVs(vsWeek.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vs"] });
      qc.invalidateQueries({ queryKey: ['vs-current'] });
      onDeleted();
    },
  });

  return (
    <div className="space-y-4">
      <EventsTabs user={user} />
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/events/vs"
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
        >
          <ArrowLeft size={14} /> Back to VS
        </Link>
        <div className="flex items-center gap-2">
          {vsWeek.outcome ? (
            <button
              onClick={() => setOutcome.mutate(null)}
              disabled={setOutcome.isPending}
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-40"
            >
              <RotateCcw size={12} /> Reopen
            </button>
          ) : (
            <>
              <button
                onClick={() => setOutcome.mutate('win')}
                disabled={setOutcome.isPending}
                className="inline-flex items-center gap-1 rounded border border-emerald-800 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40 disabled:opacity-40"
              >
                <Trophy size={12} /> Win
              </button>
              <button
                onClick={() => setOutcome.mutate('loss')}
                disabled={setOutcome.isPending}
                className="inline-flex items-center gap-1 rounded border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40 disabled:opacity-40"
              >
                <XCircle size={12} /> Lose
              </button>
            </>
          )}
          {!locked && (
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1 rounded border border-indigo-800 px-2 py-1 text-xs text-indigo-200 hover:bg-indigo-900/40"
            >
              <Upload size={12} /> Import video
            </button>
          )}
          <button
            onClick={() => {
              if (confirm(`Delete VS entry ${vsWeek.weekStart}? This also deletes all scores.`)) {
                remove.mutate();
              }
            }}
            disabled={remove.isPending}
            className="inline-flex items-center gap-1 rounded border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40 disabled:opacity-40"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>

      <VsHeader detail={detail} />

      {jobs.length > 0 && (
        <VsImportJobsStrip
          vsId={vsWeek.id}
          jobs={jobs}
          onOpen={setOpenJobId}
          disabled={locked}
        />
      )}

      <TopPlayersCard
        vsId={vsWeek.id}
        players={activePlayers}
        topPlayers={detail.topPlayers}
        disabled={locked}
      />

      <div className="flex gap-2">
        <button
          onClick={() => setView('weekly')}
          className={cn(
            'rounded border px-3 py-1.5 text-sm',
            view === 'weekly'
              ? 'border-indigo-500 bg-indigo-900/40 text-white'
              : 'border-slate-700 text-slate-300 hover:bg-slate-800',
          )}
        >
          Weekly total
        </button>
        <button
          onClick={() => setView('daily')}
          className={cn(
            'rounded border px-3 py-1.5 text-sm',
            view === 'daily'
              ? 'border-indigo-500 bg-indigo-900/40 text-white'
              : 'border-slate-700 text-slate-300 hover:bg-slate-800',
          )}
        >
          Daily
        </button>
      </div>

      {view === 'daily' && (
        <DailyGrid
          vsId={vsWeek.id}
          players={activePlayers}
          dailyScores={detail.dailyScores}
          disabled={locked}
        />
      )}

      {importOpen && (
        <ImportVsDialog
          vsId={vsWeek.id}
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={() => qc.invalidateQueries({ queryKey: ['vs', vsWeek.id] })}
        />
      )}
      {openJobId && (
        <ImportVsDialog
          vsId={vsWeek.id}
          open={true}
          initialJobId={openJobId}
          onClose={() => setOpenJobId(null)}
          onImported={() => qc.invalidateQueries({ queryKey: ['vs', vsWeek.id] })}
        />
      )}

      {view === 'weekly' && (
      <section className="w-fit max-w-full overflow-x-auto rounded-lg border border-slate-800">
        <div className="border-b border-slate-800 bg-slate-900/60 px-3 py-2 text-xs text-slate-400">
          Weekly totals are computed automatically from daily scores — edit
          them in the <strong className="font-semibold text-slate-200">Daily</strong> view.
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">Player</th>
              <th className="px-3 py-2">Rank</th>
              <th className="px-3 py-2 text-right">Score (M)</th>
              <th className="px-3 py-2 text-right">Last update</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {activePlayers.map((p) => (
              <ScoreRow
                key={p.id}
                player={p}
                value={inputs.get(p.id) ?? ''}
                state={rowState.get(p.id)}
                updatedAt={updatedAtMap.get(p.id)}
                disabled
                onChange={() => {}}
                onCommit={() => {}}
              />
            ))}
            {activePlayers.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                  No active players.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-slate-900/70 text-sm">
            <tr>
              <td colSpan={2} className="px-3 py-2 text-slate-400">
                {total.count} scored · avg{' '}
                <span className="tabular-nums">
                  {total.count > 0 ? Math.round(total.sum / total.count).toLocaleString() : '—'}
                </span>{' '}
                M
              </td>
              <td className="px-3 py-2 text-right font-medium tabular-nums">
                {total.sum.toLocaleString()} M
              </td>
              <td className="px-3 py-2" />
            </tr>
          </tfoot>
        </table>
      </section>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Daily grid: Mon–Sat cells per active player, inline editable.
// -----------------------------------------------------------------------------

function DailyGrid({
  vsId,
  players,
  dailyScores,
  disabled,
}: {
  vsId: number;
  players: VsDetailPlayer[];
  dailyScores: VsDailyScore[];
  disabled: boolean;
}) {
  const qc = useQueryClient();

  // Threshold per day (M): SVS/VS policy defines redAbsoluteM as the weekly
  // low-total ceiling; a daily "MET" cell needs 1/6 of that. Displayed in
  // legend below; kept local so the grid doesn't need to fetch policy.
  const policyQuery = useQuery({
    queryKey: ['vs-policy'],
    queryFn: () => api.getVsPolicy(),
    staleTime: 60_000,
  });
  const dailyThresholdM = (policyQuery.data?.redAbsoluteM ?? 43.2) / 6;

  const scoreMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of dailyScores) m.set(`${s.playerId}-${s.dayIndex}`, s.scoreTenthsM);
    return m;
  }, [dailyScores]);

  const [editing, setEditing] = useState<{ playerId: number; day: number } | null>(null);
  const [draft, setDraft] = useState('');

  const save = useMutation({
    mutationFn: (entry: { playerId: number; dayIndex: number; scoreTenthsM: number | null }) =>
      api.saveVsDailyScores(vsId, [entry]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vs', vsId] }),
  });

  function commit() {
    if (!editing) return;
    const parsed = parseMillionsOneDecimal(draft);
    save.mutate({
      playerId: editing.playerId,
      dayIndex: editing.day,
      scoreTenthsM: parsed,
    });
    setEditing(null);
    setDraft('');
  }

  function cellClass(tenths: number | undefined) {
    if (tenths == null) return 'border-slate-800 bg-slate-950 text-slate-500';
    const m = tenths / 10;
    return m >= dailyThresholdM
      ? 'border-emerald-800/60 bg-emerald-950/40 text-emerald-200'
      : 'border-red-800/60 bg-red-950/40 text-red-200';
  }

  return (
    <section className="space-y-2">
      <div className="w-fit max-w-full overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-auto text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-2 py-1">Player</th>
              {DAY_LABELS_UPPER.map((d) => (
                <th key={d} className="px-1 py-1 text-center">
                  {d}
                </th>
              ))}
              <th className="px-2 py-1 text-right">Σ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {players.map((p) => {
              let total = 0;
              return (
                <tr key={p.id}>
                  <td className="whitespace-nowrap px-2 py-0.5 font-medium">
                    {p.displayName?.trim() || p.name}
                  </td>
                  {DAY_LABELS_UPPER.map((_, i) => {
                    const t = scoreMap.get(`${p.id}-${i}`);
                    if (t != null) total += t;
                    const isEditing = editing?.playerId === p.id && editing.day === i;
                    return (
                      <td key={i} className="px-0.5 py-0.5 text-center">
                        {isEditing ? (
                          <input
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={commit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commit();
                              else if (e.key === 'Escape') {
                                setEditing(null);
                                setDraft('');
                              }
                            }}
                            className="w-12 rounded border border-slate-500 bg-slate-950 px-1 py-0.5 text-center tabular-nums text-white"
                          />
                        ) : (
                          <button
                            disabled={disabled}
                            onClick={() => {
                              setEditing({ playerId: p.id, day: i });
                              setDraft(t != null ? formatMillionsOneDecimal(t) : '');
                            }}
                            className={cn(
                              'inline-block w-12 rounded border px-1 py-0.5 text-center tabular-nums transition-colors',
                              cellClass(t),
                              !disabled && 'hover:border-slate-500',
                              disabled && 'cursor-not-allowed opacity-70',
                            )}
                          >
                            {t != null ? formatMillionsOneDecimal(t) : '—'}
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td className="whitespace-nowrap px-2 py-0.5 text-right tabular-nums text-slate-300">
                    {total > 0 ? formatMillionsOneDecimal(total) : '—'}
                  </td>
                </tr>
              );
            })}
            {players.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                  No active players.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-4 rounded border border-red-800/60 bg-red-950/40" />
          MISSED (&lt; {dailyThresholdM.toFixed(1)} M)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-4 rounded border border-emerald-800/60 bg-emerald-950/40" />
          MET (≥ {dailyThresholdM.toFixed(1)} M)
        </span>
      </div>
    </section>
  );
}

function ScoreRow({
  player,
  value,
  state,
  updatedAt,
  disabled,
  onChange,
  onCommit,
}: {
  player: VsDetailPlayer;
  value: string;
  state: RowState | undefined;
  updatedAt: string | undefined;
  disabled: boolean;
  onChange: (v: string) => void;
  onCommit: (v: string) => void;
}) {
  return (
    <tr>
      <td className="px-3 py-2 font-medium">{player.displayName?.trim() || player.name}</td>
      <td className="px-3 py-2 text-slate-400">{RANK_LABEL[player.rank]}</td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-2">
          <StatusIcon state={state} />
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={disabled}
            className={cn(
              'w-24 rounded border px-2 py-1 text-right tabular-nums focus:outline-none',
              state === 'error'
                ? 'border-red-700 bg-red-950/40 focus:border-red-500'
                : 'border-slate-700 bg-slate-950 focus:border-slate-500',
              disabled && 'cursor-not-allowed opacity-60',
            )}
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={(e) => onCommit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            placeholder="—"
          />
        </div>
      </td>
      <td className="px-3 py-2 text-right text-xs text-slate-500 tabular-nums">
        {updatedAt ? updatedAt.slice(0, 10) : '—'}
      </td>
    </tr>
  );
}

function StatusIcon({ state }: { state: RowState | undefined }) {
  if (state === 'saving') return <Loader2 size={12} className="animate-spin text-slate-400" />;
  if (state === 'saved') return <Check size={12} className="text-emerald-400" />;
  if (state === 'error') return <span className="text-xs text-red-400">!</span>;
  return <span className="inline-block w-3" />;
}

function VsHeader({ detail }: { detail: VsDetail }) {
  const { vs: vsWeek } = detail;
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [weekStart, setWeekStart] = useState(vsWeek.weekStart);
  const [kind, setKind] = useState<VsKind>(vsWeek.kind);
  const [opponentName, setOpponentName] = useState(vsWeek.opponentName);
  const [opponentServer, setOpponentServer] = useState(vsWeek.opponentServer);
  const [error, setError] = useState<string | null>(null);
  const locked = !!vsWeek.lockedAt;

  const save = useMutation({
    mutationFn: () =>
      api.updateVs(vsWeek.id, {
        weekStart,
        kind,
        opponentName: opponentName.trim(),
        opponentServer: opponentServer.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vs", vsWeek.id] });
      qc.invalidateQueries({ queryKey: ["vs"] });
      qc.invalidateQueries({ queryKey: ['vs-current'] });
      setEditing(false);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  if (!editing) {
    return (
      <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-lg font-semibold tabular-nums">{vsWeek.weekStart}</div>
          <KindPill kind={vsWeek.kind} />
          {vsWeek.outcome && <OutcomePill outcome={vsWeek.outcome} />}
          {locked && !vsWeek.outcome && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-900/40 px-2 py-0.5 text-xs text-amber-200">
              <Lock size={11} /> Locked
            </span>
          )}
          <div className="text-sm text-slate-300">
            vs <span className="font-medium text-white">{vsWeek.opponentName}</span>{' '}
            <span className="text-slate-500">{vsWeek.opponentServer}</span>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="ml-auto rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
          >
            Edit
          </button>
        </div>
        <NotesField vs={vsWeek} />
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,150px)_auto_minmax(0,1fr)_minmax(0,140px)]">
        <label className="block text-xs text-slate-400">
          Week start
          <input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm"
          />
        </label>
        <label className="block text-xs text-slate-400">
          Kind
          <div className="mt-1 flex w-fit rounded-lg border border-slate-700 bg-slate-950 p-0.5 text-sm">
            {(['save', 'soft_push', 'push', 'aborted_push'] as const).map((k) => (
              <button
                type="button"
                key={k}
                onClick={() => setKind(k)}
                className={cn(
                  'rounded px-3 py-1 transition',
                  kind === k ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white',
                )}
              >
                {k === 'push'
                  ? 'Push'
                  : k === 'save'
                    ? 'Save'
                    : k === 'soft_push'
                      ? 'Soft'
                      : 'Abort'}
              </button>
            ))}
          </div>
        </label>
        <label className="block text-xs text-slate-400">
          Opponent
          <input
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm"
            value={opponentName}
            onChange={(e) => setOpponentName(e.target.value)}
          />
        </label>
        <label className="block text-xs text-slate-400">
          Server
          <input
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm"
            value={opponentServer}
            onChange={(e) => setOpponentServer(e.target.value)}
          />
        </label>
      </div>
      <NotesField vs={vsWeek} />
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          onClick={() => {
            setEditing(false);
            setWeekStart(vsWeek.weekStart);
            setKind(vsWeek.kind);
            setOpponentName(vsWeek.opponentName);
            setOpponentServer(vsWeek.opponentServer);
            setError(null);
          }}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}

function OutcomePill({ outcome }: { outcome: VsOutcome }) {
  const isWin = outcome === 'win';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
        isWin ? 'bg-emerald-900/50 text-emerald-200' : 'bg-red-900/50 text-red-200',
      )}
    >
      {isWin ? <Trophy size={11} /> : <XCircle size={11} />}
      {isWin ? 'Win' : 'Loss'}
    </span>
  );
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function TopPlayersCard({
  vsId,
  players,
  topPlayers,
  disabled,
}: {
  vsId: number;
  players: VsDetailPlayer[];
  topPlayers: VsTopPlayerEntry[];
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const initial = useMemo(() => {
    const map = new Map<number, number | null>();
    for (let i = 0; i < 6; i++) map.set(i, null);
    for (const t of topPlayers) map.set(t.dayIndex, t.playerId);
    return map;
  }, [topPlayers]);

  const [assignments, setAssignments] = useState<Map<number, number | null>>(initial);
  useEffect(() => {
    setAssignments(initial);
  }, [initial]);

  const usedPlayerIds = useMemo(() => {
    const s = new Set<number>();
    for (const pid of assignments.values()) {
      if (pid != null) s.add(pid);
    }
    return s;
  }, [assignments]);

  const playerLabel = (p: VsDetailPlayer) => p.displayName?.trim() || p.name;

  const commit = async (dayIndex: number, playerId: number | null) => {
    const prev = initial.get(dayIndex) ?? null;
    if (prev === playerId) return;
    setState('saving');
    try {
      await api.saveVsTopPlayers(vsId, [{ dayIndex, playerId }]);
      qc.invalidateQueries({ queryKey: ["vs", vsId] });
      setState('saved');
      setTimeout(() => setState('idle'), 1200);
    } catch {
      setState('error');
      setAssignments(initial);
    }
  };

  return (
    <section className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-400">
        <span>Best Player of the Day</span>
        {state === 'saving' && <Loader2 size={11} className="animate-spin" />}
        {state === 'saved' && <Check size={11} className="text-emerald-400" />}
        {state === 'error' && <span className="text-red-400">save failed</span>}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {DAY_LABELS.map((label, dayIndex) => {
          const selectedId = assignments.get(dayIndex) ?? null;
          const selected = selectedId ? players.find((p) => p.id === selectedId) ?? null : null;
          const options = players.filter(
            (p) => p.id === selectedId || !usedPlayerIds.has(p.id),
          );
          return (
            <div key={dayIndex} className="text-xs text-slate-400">
              <div className="mb-1">{label}</div>
              <TopPlayerPicker
                players={options}
                selected={selected}
                disabled={disabled}
                onSelect={(id) => {
                  setAssignments((prev) => {
                    const next = new Map(prev);
                    next.set(dayIndex, id);
                    return next;
                  });
                  void commit(dayIndex, id);
                }}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TopPlayerPicker({
  players,
  selected,
  disabled,
  onSelect,
}: {
  players: VsDetailPlayer[];
  selected: VsDetailPlayer | null;
  disabled: boolean;
  onSelect: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => {
      const name = (p.displayName?.trim() || p.name).toLowerCase();
      return name.includes(q) || p.name.toLowerCase().includes(q);
    });
  }, [players, query]);

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    setQuery('');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (id: number | null) => {
    onSelect(id);
    setOpen(false);
  };

  const label = selected ? selected.displayName?.trim() || selected.name : null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center justify-between gap-1 rounded border px-2 py-1 text-left text-sm',
          selected
            ? 'border-slate-700 bg-slate-950 text-slate-100 hover:border-slate-600'
            : 'border-dashed border-slate-700 bg-slate-950/60 text-slate-500 hover:border-slate-600',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className="truncate">
          {label ?? <span className="text-slate-500">—</span>}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-slate-500">
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                pick(null);
              }}
              className="rounded p-0.5 hover:bg-slate-800 hover:text-red-400"
              aria-label="Clear"
            >
              <X size={12} />
            </span>
          )}
          <Search size={12} />
        </span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => Math.min(filtered.length - 1, h + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(0, h - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const p = filtered[highlight];
                if (p) pick(p.id);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder="Search player…"
            className="w-full border-b border-slate-800 bg-slate-950 px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">No match.</li>
            )}
            {filtered.map((p, i) => {
              const l = p.displayName?.trim() || p.name;
              const isSel = selected?.id === p.id;
              const isHi = i === highlight;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(p.id)}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-1.5 text-left text-sm',
                      isHi && 'bg-slate-800',
                      isSel && 'text-indigo-300',
                    )}
                  >
                    <span className="truncate">{l}</span>
                    <span className="ml-2 shrink-0 text-xs text-slate-500">
                      {p.rank}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function NotesField({ vs: vsWeek }: { vs: VsDetail['vs'] }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(vsWeek.notes ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    setValue(vsWeek.notes ?? '');
  }, [vsWeek.notes]);

  const commit = async () => {
    const next = value.trim();
    const current = (vsWeek.notes ?? '').trim();
    if (next === current) return;
    setState('saving');
    try {
      await api.updateVs(vsWeek.id, { notes: next || null });
      setState('saved');
      qc.invalidateQueries({ queryKey: ["vs", vsWeek.id] });
      qc.invalidateQueries({ queryKey: ["vs"] });
      setTimeout(() => setState('idle'), 1500);
    } catch {
      setState('error');
    }
  };

  return (
    <label className="block text-xs text-slate-400">
      <span className="flex items-center gap-2">
        Notes
        {state === 'saving' && <Loader2 size={11} className="animate-spin" />}
        {state === 'saved' && <Check size={11} className="text-emerald-400" />}
        {state === 'error' && <span className="text-red-400">save failed</span>}
      </span>
      <textarea
        className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
        rows={2}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        placeholder="e.g. Save mode since Wednesday"
      />
    </label>
  );
}

// -----------------------------------------------------------------------------
// Import jobs strip: shows recent unapplied video imports for this VS week.
// Users can reopen a done job to import, or delete finished/failed jobs.
// -----------------------------------------------------------------------------

function VsImportJobsStrip({
  vsId,
  jobs,
  onOpen,
  disabled,
}: {
  vsId: number;
  jobs: VsImportVideoJob[];
  onOpen: (jobId: string) => void;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (jobId: string) => api.deleteVsImportJob(vsId, jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vs-import-jobs', vsId] }),
  });

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
      <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
        Video imports
      </div>
      <ul className="divide-y divide-slate-800">
        {jobs.map((j) => (
          <li key={j.id} className="flex items-center gap-3 py-2 text-sm">
            <JobStatusChip status={j.status} stage={j.stage} />
            <div className="flex-1 min-w-0">
              <div className="text-slate-200">
                {j.kind === 'weekly' ? 'Weekly total' : 'Daily'}
                {j.kind === 'daily' && j.requestedDayIndex != null && (
                  <span className="text-slate-500"> · {DAY_LABELS_UPPER[j.requestedDayIndex]}</span>
                )}
                {j.error && (
                  <span className="ml-2 text-red-400 truncate">· {j.error}</span>
                )}
              </div>
              <div className="text-xs text-slate-500">
                {j.createdAt?.replace('T', ' ').slice(0, 16)} ·{' '}
                {j.videoBytes ? `${Math.round((j.videoBytes / 1024 / 1024) * 10) / 10} MB` : ''}
              </div>
            </div>
            {(j.status === 'done' || j.status === 'needs_kind_pick') && !disabled && (
              <button
                onClick={() => onOpen(j.id)}
                className="rounded border border-emerald-800 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40"
              >
                Open preview
              </button>
            )}
            {(j.status === 'queued' || j.status === 'running') && (
              <button
                onClick={() => onOpen(j.id)}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                Watch
              </button>
            )}
            {j.status === 'failed' && !disabled && (
              <button
                onClick={() => onOpen(j.id)}
                className="rounded border border-red-800 px-2 py-1 text-xs text-red-200 hover:bg-red-900/40"
              >
                Retry
              </button>
            )}
            {j.status !== 'running' && (
              <button
                onClick={() => {
                  if (confirm('Delete this import job? The stored video will be removed.')) {
                    del.mutate(j.id);
                  }
                }}
                className="rounded border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40"
                title="Delete job"
              >
                <Trash2 size={12} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function JobStatusChip({ status, stage }: { status: VsImportVideoJob['status']; stage: string }) {
  const map: Record<VsImportVideoJob['status'], { label: string; cls: string }> = {
    queued: { label: 'queued', cls: 'bg-slate-800 text-slate-300 border-slate-700' },
    running: {
      label: stage === 'uploading' ? 'uploading' : stage === 'processing' ? 'extracting' : 'running',
      cls: 'bg-indigo-950/60 text-indigo-200 border-indigo-800',
    },
    done: { label: 'ready', cls: 'bg-emerald-950/60 text-emerald-200 border-emerald-800' },
    needs_kind_pick: { label: 'needs pick', cls: 'bg-amber-950/60 text-amber-200 border-amber-800' },
    failed: { label: 'failed', cls: 'bg-red-950/60 text-red-200 border-red-800' },
    discarded: { label: 'discarded', cls: 'bg-slate-800 text-slate-500 border-slate-700' },
  };
  const cfg = map[status];
  return (
    <span
      className={
        'inline-flex w-24 justify-center rounded border px-2 py-0.5 text-xs ' + cfg.cls
      }
    >
      {cfg.label}
    </span>
  );
}
